import { msg } from "@omegagrid/localize";
import { NodeSource, TreeAdapter, TreeOptions, TreeSource } from "@omegagrid/tree";
import rp from "@reporting/rp-app-host";
import { Accounting, AccountingGroup, AccountingSettings } from "@reporting/rp-types/core";
import { buildAccountingTree } from "./accountingTree";

export type AccountingTreeOptions = {
	showAccountingNumber?: boolean;
	idGroup?: number;
	treeOptions?: Partial<TreeOptions>;
	showAllItem?: boolean;
	excludedAccounting?: number;
	right?: string;
	byGroups?: boolean;
}

export class AccountingTreeAdapter extends TreeAdapter {

	private items: NodeSource[];

	constructor(
		private options?: Partial<AccountingTreeOptions>
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
		const accountings = await rp.ms('accounting').get<Accounting[]>(`accountings${this.options?.idGroup ? `/${this.options.idGroup}` : ''}`, {
			where: this.options?.excludedAccounting ? `ACCOUNTING <> ${this.options.excludedAccounting}` : '',
			right: this.options?.right ?? '',
			...(this.options?.byGroups ? { order: ['ACCOUNTING'] } : {}),
		});
		const label = (a: Accounting) => this.options?.showAccountingNumber === false ? a.company : `${a.accounting} ${a.company}`;
		this.items = this.options?.byGroups
			? buildAccountingTree(accountings, label)
			: accountings.map(a => ({ k: a.accounting.toString(), v: label(a) }));
		if (this.options?.showAllItem) {
			this.items.unshift({ k: null, v: `<${msg('all')}>` });
		}
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options?.treeOptions || {};
	}

}

export class GroupTreeAdapter extends TreeAdapter {

	private items: NodeSource[];

	constructor(
		private options?: Partial<TreeOptions>,
		private showAllItem: boolean = false,
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
		const groups = await rp.ms('accounting').get<AccountingGroup[]>(`groups`, { fields: ['id_group', 'group_name'] });
		this.items = groups.map(g => ({k: g.id_group.toString(), v: `${g.id_group} ${g.group_name}`}));
		if (this.showAllItem) {
			this.items.unshift({ k: null, v: `<${msg('all')}>` });
		}
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {};
	}

}

export type AccountingGroupTreeOptions = {
	/** accounting (ACCOUNTING) */
	a?: string,
	/** group (ID_ACCOUNTING_GROUP) */
	g?: string,
	/** server (ID_SERVER) */
	s?: string,
}

export type AccountingGroupTreeAdapterOptions = Partial<TreeOptions> & {
	/** the groups can be picked like the companies (default); false leaves them as headings only */
	selectableGroups?: boolean,
}

export class AccountingGroupTreeAdapter extends TreeAdapter {

	private items: NodeSource[];

	constructor(
		public params?: AccountingGroupTreeOptions,
		private options?: AccountingGroupTreeAdapterOptions,
		private showAllItem: boolean = false,
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
		const accountings = await rp.ms('accounting').get<AccountingSettings[]>('groupTree', this.params);
		this.items = [];
		for (const a of accountings) {
			this.items.push(!a.accounting ? { // group header row
				l: 0,
				k: `g-${a.id_group}`,
				v: `${a.id_group} ${a.group_name}`,
				collapsed: true,
				...(this.options?.selectableGroups === false ? { ch: false } : {}),
			} : {
				l: a.level + 1,
				k: `a-${a.accounting}`,
				v: `${a.accounting} ${a.company}`,
				collapsed: true
			});
		}
		if (this.showAllItem) {
			this.items.unshift({ k: null, v: `<${msg('all')}>` });
		}
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		// the adapter's own options are not options of the tree
		const options: AccountingGroupTreeAdapterOptions = { ...this.options };
		delete options.selectableGroups;
		return options;
	}

}

export class AvailableAccountingTreeAdapter extends TreeAdapter {

	private items: NodeSource[];

	constructor(
		private idGroup: number,
		private options?: Partial<TreeOptions>,
	) { super() }

	async getTree(): Promise<TreeSource> {
		if (this.items) return this.items;
		this.items = await rp.ms('accounting').get(`availableAccountings/${this.idGroup}`);
		return this.items;
	}

	getOptions(): Partial<TreeOptions> {
		return this.options || {};
	}

}
